import { BsCheck } from "@react-icons/all-files/bs/BsCheck";
import { memo } from "react";
import styled from "styled-components";

import {
  useCheckHalHourglass,
  useCheckSilmaelExchange,
} from "@core/hooks/mutations/todo";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character } from "@core/types/character";

type WeekCheckType = "silmaelChange" | "halHourglass";

interface Props {
  character: Character;
  type: WeekCheckType;
}

/**
 * 주간 숙제 boolean 셀 (실마엘 혈석 교환 / 할의 모래시계).
 * - 클릭: 토글
 * - 설정 꺼져 있으면 "-" 표시
 */
const WeekCheckCell = ({ character, type }: Props) => {
  const show =
    type === "silmaelChange"
      ? character.settings.showSilmaelChange
      : character.settings.showHalHourglass;

  const checked =
    type === "silmaelChange" ? character.silmaelChange : character.halHourglass;

  const checkSilmael = useCheckSilmaelExchange({
    onSuccess: (updatedCharacter) => {
      updateCharacterQueryData({ character: updatedCharacter });
    },
  });

  const checkHalHourglass = useCheckHalHourglass({
    onSuccess: (updatedCharacter) => {
      updateCharacterQueryData({ character: updatedCharacter });
    },
  });

  if (!show) {
    return <Empty>-</Empty>;
  }

  const handleClick = () => {
    if (type === "silmaelChange") {
      checkSilmael.mutate({ characterId: character.characterId });
    } else {
      checkHalHourglass.mutate({ characterId: character.characterId });
    }
  };

  return (
    <Wrapper
      type="button"
      onClick={handleClick}
      $isDone={checked}
      aria-label={
        type === "silmaelChange" ? "실마엘 혈석 교환 토글" : "할의 모래시계 토글"
      }
    >
      <Indicator $isDone={checked}>{checked ? <BsCheck /> : ""}</Indicator>
    </Wrapper>
  );
};

export default memo(WeekCheckCell);

const Wrapper = styled.button<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: ${({ $isDone }) => ($isDone ? 0.4 : 1)};

  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const Indicator = styled.div<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.yellow[300] : "transparent"};
  color: ${({ $isDone, theme }) =>
    $isDone ? theme.app.palette.gray[0] : theme.app.text.dark2};

  svg {
    font-size: 18px;
  }
`;

const Empty = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 44px;
  color: ${({ theme }) => theme.app.text.light2};
  font-size: 13px;
`;
